import { useDates } from "dates/DatesContext";
import { useFetch } from "hooks/api";
import moment from "moment";
import { useMemo } from "react";
import { formatDatamartFilters } from "utils/formatV2DatamartFilters";
import { mergeTrendData } from "utils/mergeTrendData";
import useDSPSellerInfo from "./hooks/useDSPSellerInfo";

const useDSPProductData = (dimensions = "asin,conversion_type", includeStats = true, filters) => {
  const { start, end, preStart, preEnd } = useDates();
  const { dspAdvertiser } = useDSPSellerInfo();

  const startDate = moment(start).format("YYYY-MM-DD");
  const endDate = moment(end).format("YYYY-MM-DD");
  const preStartDate = moment(preStart).format("YYYY-MM-DD");
  const preEndDate = moment(preEnd).format("YYYY-MM-DD");

  const formattedFilters = formatDatamartFilters({ ...filters, dsp_seller_info_id: dspAdvertiser?.id });

  const { data: dspProductMetadata, isLoading: isDSPProductMetadataLoading } = useFetch(
    ["dspProductMetadata", startDate, endDate, dimensions, formattedFilters],
    "/api/data_report_v2/dspproduct/entities/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: startDate,
        date__lte: endDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => {
        return res.data;
      },
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const { data: dspProductData, isLoading: isDSPProductDataLoading } = useFetch(
    ["dspProductData", startDate, endDate, dimensions, formattedFilters],
    "/api/data_report_v2/dspproduct/stats/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: startDate,
        date__lte: endDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => res.data,
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const { data: prevPeriodDSPProductData, isLoading: isPrevPeriodDSPProductDataLoading } = useFetch(
    ["prevPeriodDSPProductData", preStartDate, preEndDate, dimensions, formattedFilters],
    "/api/data_report_v2/dspproduct/stats/",
    {
      dimensions,
      filters: formatDatamartFilters({
        ...filters,
        date__gte: preStartDate,
        date__lte: preEndDate,
        dsp_seller_info_id: dspAdvertiser?.id,
      }),
    },
    {
      default: [],
      select: (res) => res.data,
      staleTime: 5 * 60 * 1000,
      enabled: !!dspAdvertiser && includeStats,
    },
  );

  const mergedTrendData = useMemo(() => {
    return mergeTrendData(dspProductData, prevPeriodDSPProductData, "asin");
  }, [dspProductData, prevPeriodDSPProductData]);

  const isLoading = isDSPProductDataLoading || isPrevPeriodDSPProductDataLoading || isDSPProductMetadataLoading;

  return { data: mergedTrendData, metadata: dspProductMetadata, isLoading };
};

export default useDSPProductData;
